import React, { useState } from 'react';

import {
  Box,
  Button,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';

import {
  COLOR_GREY_600,
  COLOR_PRIMARY_2,
  COLOR_TEXT_4,
  COLOR_TEXT_GREY,
  SIDEBAR_COLOR,
  SUBSCRIPTION_MODAL_DARK_THEME_COLOR,
} from 'src/utils/colors';
import { exchangeRates, PRICING_TABLE_TOOLTIPS } from 'src/utils/const';
import {
  calculateCouponChange,
  checkPromocodeValidity,
  formatCurrencyBasedOnCountryCode,
  isCurrentPlanSubscribed,
} from 'src/utils/functions';
import { InfoButton } from 'src/App/Dashboards/Market/Components/Buttons';
import InfoTooltipContent from 'src/App/Dashboards/Market/Components/Tooltips/InfoTooltipContent';

const SubscriptionTierCard = ({
  plan,
  title,
  description,
  features,
  subFeatures,
  prevTier,
  userLocalCurrency,
  user,
  duration,
  subscriptionPriceAccordingToUserLocalCurrency,
  promoCodeDiscountsDict,
  product,
  setProduct,
  promoCode,
  isPromoCodeValid,
  openDemoBookingForm,
  paymentModalRef,
  setFormHeadingFor,
  setShowUpgradeSubscriptionModal,
}) => {
  const currentActivePlan = user.subscriptionPlan;

  let price: any = 0;
  let priceDiscountedMonthly: any = 0;
  let priceDiscountedYearly: any = 0;

  let isPlanSubscribed = isCurrentPlanSubscribed(plan, currentActivePlan);

  const [showAllFeatures, setShowAllFeatures] = useState<boolean>(false);

  if (plan != 'starter') {
    price =
      duration == 'monthly'
        ? formatCurrencyBasedOnCountryCode(
            parseFloat(subscriptionPriceAccordingToUserLocalCurrency[plan]['monthly'].toFixed(2)),
            userLocalCurrency
          )
        : formatCurrencyBasedOnCountryCode(
            parseFloat(
              (subscriptionPriceAccordingToUserLocalCurrency[plan][duration] / 12).toFixed()
            ),
            userLocalCurrency
          );

    if (isPromoCodeValid) {
      let dPrice: any = calculateCouponChange(
        promoCode,
        promoCodeDiscountsDict,
        subscriptionPriceAccordingToUserLocalCurrency,
        duration,
        () => {},
        plan
      );

      priceDiscountedMonthly = formatCurrencyBasedOnCountryCode(
        parseFloat((dPrice / 12).toFixed(2)),
        userLocalCurrency
      );
      priceDiscountedYearly = formatCurrencyBasedOnCountryCode(dPrice, userLocalCurrency);
    }
  }

  const handleBuyNowBtnclick = chosenPlan => {
    const easeInOutQuad = t => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

    if (chosenPlan == 'starter') {
      return;
    }

    setProduct(chosenPlan);

    if (
      (['expert', 'scale'].includes(chosenPlan) && currentActivePlan == 'pro') ||
      (chosenPlan == 'scale' && currentActivePlan == 'expert')
    ) {
      setShowUpgradeSubscriptionModal(chosenPlan);
      return;
    }

    if (chosenPlan != 'starter') {
      if (paymentModalRef.current) {
        paymentModalRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const generateFeatureList = (features: any) => {
    return (
      <List disablePadding={true} dense={true}>
        {features.map((feature, index) => (
          <ListItem key={index} disableGutters={false} dense={true}>
            <ListItemIcon sx={{ minWidth: '30px', marginRight: '0px' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                  borderRadius: '900px',
                  backgroundColor: '#ccc3',
                  padding: '8px',
                  marginRight: '10px',
                }}
              >
                {feature.icon ? (
                  <feature.icon sx={{ color: COLOR_PRIMARY_2 }} />
                ) : (
                  <DoneRoundedIcon sx={{ color: COLOR_PRIMARY_2, fontSize: '20px' }} />
                )}
              </Box>
            </ListItemIcon>

            <ListItemText
              primary={
                <>
                  {feature.title}
                  <InfoButton
                    iconColor={COLOR_TEXT_GREY}
                    tooltipContent={feature.tooltip}
                    extraStyles={{
                      marginLeft: '4px',
                      transform: 'translateY(4px)',
                      display: 'inline-block !important',
                      width: 'auto',
                    }}
                  />
                </>
              }
              sx={{
                maxWidth: '100%',
              }}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Box
      sx={{
        background: 'white',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.25)',
        borderRadius: 5,
        pt: 5,
        pb: 5,
        pl: 4,
        pr: 4,
      }}
    >
      {/* {plan == 'expert' && (
        <Box
          sx={{
            backgroundColor: SUBSCRIPTION_MODAL_DARK_THEME_COLOR,
            padding: '5px 10px',
            mb: 2,
            borderRadius: 10,
            width: 'fit-content',
          }}
        >
          <Typography style={{ fontSize: '12px', fontWeight: 500, color: SIDEBAR_COLOR }}>
            Popular
          </Typography>
        </Box>
      )} */}

      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
        {title.split(' ').length > 2 ? (
          <>
            {title.split(' ')[0]} {title.split(' ')[1]}{' '}
            <span style={{ color: COLOR_PRIMARY_2 }}>{title.split(' ')[2]}</span>
          </>
        ) : (
          <>
            {title.split(' ')[0]}{' '}
            <span style={{ color: COLOR_PRIMARY_2 }}>{title.split(' ')[1]}</span>
          </>
        )}
      </Typography>

      <Typography variant="body1" sx={{ mt: 3, color: COLOR_TEXT_4 }}>
        {description}
      </Typography>

      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Original pricing */}
          <Typography
            variant={isPromoCodeValid && plan !== 'starter' ? 'h6' : 'h4'}
            sx={{
              fontWeight: 'bold',
              textDecoration: isPromoCodeValid && plan !== 'starter' ? 'line-through' : 'none',
              display: 'inline',
            }}
          >
            {exchangeRates[userLocalCurrency].symbol}
            {price}
          </Typography>

          {/* Promo code discounted pricing */}
          {isPromoCodeValid && plan !== 'starter' && (
            <Typography
              variant="h4"
              style={{
                fontWeight: 'bold',
                marginLeft: '5px',
                display: 'inline',
              }}
            >
              {exchangeRates[userLocalCurrency].symbol}
              {duration == 'yearly' ? priceDiscountedMonthly : priceDiscountedYearly}
            </Typography>
          )}
          <Typography
            variant="body1"
            sx={{
              color: COLOR_GREY_600,
              alignSelf: 'flex-end',
              transform: isPromoCodeValid ? 'translateY(-4px)' : 'translateY(-4px)',
            }}
          >
            /month
          </Typography>
        </Box>

        {/* Billed annually section */}
        {duration == 'yearly' && plan !== 'starter' && (
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              mt: 1,
            }}
          >
            Billed Annually: {exchangeRates[userLocalCurrency].symbol}
            {isPromoCodeValid
              ? priceDiscountedYearly
              : formatCurrencyBasedOnCountryCode(
                  parseFloat(
                    subscriptionPriceAccordingToUserLocalCurrency[plan]['yearly'].toFixed(2)
                  ),
                  userLocalCurrency
                )}
          </Typography>
        )}
      </Box>

      <Divider orientation="horizontal" flexItem sx={{ mt: 2, mb: 2 }} />

      {generateFeatureList(features)}

      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          mt: 3,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          cursor: 'pointer',
        }}
        onClick={() => setShowAllFeatures(prev => !prev)}
      >
        <KeyboardArrowRightRoundedIcon
          sx={{
            color: COLOR_TEXT_GREY,
            transform: showAllFeatures ? 'rotate(0deg)' : 'rotate(90deg)',
          }}
        />{' '}
        <span style={{ color: COLOR_PRIMARY_2, fontWeight: 600 }}>
          Show {showAllFeatures ? 'less' : 'all'}
        </span>
      </Typography>

      <Collapse in={showAllFeatures}>{generateFeatureList(subFeatures)}</Collapse>

      <Button
        fullWidth
        size="small"
        sx={{
          border: isPlanSubscribed ? `1px solid ${COLOR_PRIMARY_2}` : 'none',
          background: isPlanSubscribed ? '#ffffff' : COLOR_PRIMARY_2,
          color: isPlanSubscribed ? COLOR_PRIMARY_2 : '#ffffff',
          '&:hover': {
            background: isPlanSubscribed ? '#ffffff' : COLOR_PRIMARY_2,
          },
          mt: 3,
        }}
        onClick={() => handleBuyNowBtnclick(plan)}
      >
        <Typography
          variant="h6"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textTransform: 'capitalize',
          }}
        >
          {isPlanSubscribed ? (
            <>
              <CheckCircleIcon sx={{ marginRight: '5px' }} /> Your Current Plan
            </>
          ) : plan === 'Insight Starter' ? (
            'Try for free'
          ) : (currentActivePlan === 'pro' && (plan === 'expert' || plan === 'scale')) ||
            (currentActivePlan === 'expert' && plan === 'scale') ? (
            'Upgrade'
          ) : (
            'Buy Now'
          )}
        </Typography>
      </Button>
    </Box>
  );
};

export default SubscriptionTierCard;
